import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

type BtnKind = 'default' | 'primary' | 'ghost';
type BtnSize = 'sm' | 'md' | 'lg';

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  kind?: BtnKind;
  size?: BtnSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  isLoading?: boolean;
  children: ReactNode;
}

const kindClasses: Record<BtnKind, string> = {
  default:
    'bg-[var(--color-surface)] text-[var(--color-ink)] border border-[var(--color-line-strong)] ' +
    'hover:bg-[var(--color-surface-2)] focus:ring-[var(--color-accent-tint)]',
  primary:
    'bg-[var(--color-accent)] text-[var(--color-on-accent)] border border-transparent ' +
    'hover:bg-[var(--color-accent-hover)] focus:ring-[var(--color-accent-tint)]',
  ghost:
    'bg-transparent text-[var(--color-ink-soft)] border border-transparent ' +
    'hover:bg-[var(--color-surface-2)] focus:ring-[var(--color-accent-tint)]',
};

const sizeClasses: Record<BtnSize, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-6 py-3 text-base gap-2',
};

export const Btn = ({
  kind = 'default',
  size = 'md',
  icon,
  iconRight,
  isLoading = false,
  disabled,
  children,
  className = '',
  ...rest
}: BtnProps) => {
  return (
    <button
      className={
        `inline-flex items-center justify-center font-medium rounded-lg transition-colors ` +
        `outline-none focus:ring-2 ` +
        `${kindClasses[kind]} ${sizeClasses[size]} ` +
        `${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${className}`
      }
      disabled={disabled || isLoading}
      {...rest}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : icon ? (
        <span className="shrink-0">{icon}</span>
      ) : null}
      <span>{children}</span>
      {iconRight && !isLoading && <span className="shrink-0">{iconRight}</span>}
    </button>
  );
};
